import { useEffect, useRef } from "react";

import { FEATUREBASE_APP_ID } from "../../constants";
import {
  useAuthContext,
  useRoleContext,
  useSubscriptionStatus,
  useSwrFetch,
} from "../../hooks";
import { ISOStringToUnixEpoch } from "../../utils";

declare global {
  interface Window {
    Featurebase: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

interface FeaturebaseTokenResponse {
  featurebaseJwt: string;
}

function getEnvPrefix(): string {
  return import.meta.env.REACT_APP_ENVIRONMENT ?? "local";
}

// TODO: Replace wrappers with updated Featurebase installation (https://help.featurebase.app/articles/2796697-installation-overview)
export function FeaturebaseWrapper() {
  const { user } = useAuthContext();
  const { role, company } = useRoleContext();
  const { subscription } = useSubscriptionStatus();
  const featurebaseIdentifiedRef = useRef(false);

  const url = user !== null ? "/user/featurebase-token" : null;

  const { data } = useSwrFetch<FeaturebaseTokenResponse>({
    url,
    errorMessage: "Failed to log in to chat support messenger.",
  });

  const jwt =
    data?.featurebaseJwt && data.featurebaseJwt.length > 0
      ? data.featurebaseJwt
      : null;

  useEffect(() => {
    if (!jwt || !user) return;

    // Initialize Featurebase queue if it doesn't exist
    if (typeof window.Featurebase !== "function") {
      window.Featurebase = function featurebaseQueue(...args: unknown[]) {
        window.Featurebase.q = window.Featurebase.q ?? [];
        window.Featurebase.q.push(args);
      };
    }

    if (!featurebaseIdentifiedRef.current) {
      const sdkAlreadyLoaded =
        document.getElementById("featurebase-sdk") !== null;

      if (!sdkAlreadyLoaded) {
        // SDK not loaded - load it first
        const script = document.createElement("script");
        script.id = "featurebase-sdk";
        script.src = "https://do.featurebase.app/js/sdk.js";
        script.async = true;
        document.head.appendChild(script);
      }

      // Tear down any anonymous instance booted on the login screen
      // (UnauthenticatedFeaturebaseWrapper) so it re-boots fresh with the JWT.
      window.Featurebase("shutdown");

      // Featurebase expects timestamps in milliseconds, ISOStringToUnixEpoch returns seconds
      const customAttributes = {
        createdAt: user ? ISOStringToUnixEpoch(user.created_at) * 1000 : null,
        role_type: role?.type ?? "",
        companies: company
          ? [
              {
                id: `${getEnvPrefix()}-${company.id}`,
                name: company.name || null,
                createdAt: ISOStringToUnixEpoch(company.created_at) * 1000,
              },
            ]
          : [],
        is_unverified_carrier: company ? company.verified_at === null : false,
        subscription_plan_name: subscription?.subscription_plan ?? "",
      };

      window.Featurebase("boot", {
        appId: FEATUREBASE_APP_ID,
        featurebaseJwt: jwt,
        theme: "light",
        language: "en",
        ...customAttributes,
      });

      featurebaseIdentifiedRef.current = true;
    }
  }, [jwt, user, role, company, subscription]);

  return null;
}
